import type { CustomerQuantityLadderRow } from "../data/customer-api-types";

/**
 * A larger tier has to be at least this much cheaper per unit to be worth
 * nudging towards when it is not also a lower total: a 1% unit-price edge
 * is noise, and sells the customer more print for a negligible saving.
 */
export const MIN_UNIT_SAVING_PCT_FOR_NUDGE = 10;

/**
 * The "make it bigger" nudge under the quantity ladder: a larger tier worth
 * stepping up to, with what it costs and what each unit then costs. Reads
 * only the ladder rows the page already shows, so the nudge can never quote
 * a price the ladder doesn't.
 */
export interface QuantityStepUp {
  quantity: number;
  /** The larger tier's total minus the selected quantity's total. Negative
   *  when the larger tier is cheaper overall; zero when it costs the same. */
  extraPence: number;
  unitPricePence: number;
  currentUnitPricePence: number;
}

interface PricedRow {
  quantity: number;
  totalPence: number;
  unitPricePence: number;
}

function toPricedRow(row: CustomerQuantityLadderRow): PricedRow | null {
  if (
    !row.available ||
    row.total_pence == null ||
    row.unit_price_pence == null
  ) {
    return null;
  }
  return {
    quantity: row.quantity,
    totalPence: row.total_pence,
    unitPricePence: row.unit_price_pence,
  };
}

/**
 * The best larger tier to offer, in order of preference:
 * 1. the nearest larger tier that costs no more in total (more for the same
 *    money, or for less -- a strictly better offer);
 * 2. else the nearest larger tier at least `MIN_UNIT_SAVING_PCT_FOR_NUDGE`
 *    cheaper per unit (a real volume discount);
 * 3. else none: a larger tier that is barely cheaper per unit is not a nudge.
 * `null` also when the selected quantity has no price or there is no larger
 * priced tier -- the page then hides the nudge.
 */
export function quantityStepUp(
  rows: readonly CustomerQuantityLadderRow[],
  selectedQuantity: number | null,
): QuantityStepUp | null {
  if (selectedQuantity === null) return null;
  const priced = rows
    .map(toPricedRow)
    .filter((row): row is PricedRow => row !== null)
    .sort((a, b) => a.quantity - b.quantity);

  const current = priced.find((row) => row.quantity === selectedQuantity);
  if (!current) return null;

  const larger = priced.filter((row) => row.quantity > selectedQuantity);
  const target =
    larger.find((row) => row.totalPence <= current.totalPence) ??
    larger.find(
      (row) =>
        row.unitPricePence * 100 <=
        current.unitPricePence * (100 - MIN_UNIT_SAVING_PCT_FOR_NUDGE),
    );
  if (!target) return null;

  return {
    quantity: target.quantity,
    extraPence: target.totalPence - current.totalPence,
    unitPricePence: target.unitPricePence,
    currentUnitPricePence: current.unitPricePence,
  };
}
